import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight, ChevronRight, Heart, Shield, Zap, Moon, Star, Eye } from 'lucide-react';

const QUESTIONS = [
  {
    q: "What do you need most right now?",
    options: [
      { label: "Inner peace & calm", icon: Heart, value: 'calm' },
      { label: "Energy & motivation", icon: Zap, value: 'energy' },
      { label: "Protection & grounding", icon: Shield, value: 'protection' },
      { label: "Clarity & focus", icon: Eye, value: 'clarity' },
    ]
  },
  {
    q: "How would you describe your sleep?",
    options: [
      { label: "I struggle to fall asleep", value: 'insomnia' },
      { label: "I wake up tired", value: 'fatigue' },
      { label: "Restless with vivid dreams", value: 'restless' },
      { label: "I sleep just fine", value: 'fine' },
    ]
  },
  {
    q: "What draws you to crystals?",
    options: [
      { label: "Spiritual growth", icon: Star, value: 'spiritual' },
      { label: "Emotional healing", icon: Heart, value: 'emotional' },
      { label: "Physical wellness", icon: Shield, value: 'physical' },
      { label: "Curiosity — I'm new!", icon: Sparkles, value: 'new' },
    ]
  },
];

const RESULTS = {
  calm: { crystal: 'Amethyst', color: '#9D4EDD', message: 'Your energy calls for the serene wisdom of Amethyst. It will quiet the noise and reconnect you with your inner stillness.' },
  energy: { crystal: 'Carnelian', color: '#F39C12', message: 'Your fire needs reigniting. Carnelian will pour vitality into your sacral chakra and awaken your creative drive.' },
  protection: { crystal: 'Black Tourmaline', color: '#2C3E50', message: 'You need an energetic shield. Black Tourmaline will absorb negativity and anchor you to the Earth\'s stabilizing force.' },
  clarity: { crystal: 'Clear Quartz', color: '#00B4D8', message: 'The master healer is calling you. Clear Quartz will amplify your intentions and cut through mental fog like a laser.' },
};

const QuickCrystalQuiz = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);

  const handleAnswer = (value) => {
    const newAnswers = [...answers, value];
    setAnswers(newAnswers);
    if (step < QUESTIONS.length - 1) {
      setStep(step + 1);
    } else {
      const primary = newAnswers[0];
      setResult(RESULTS[primary] || RESULTS.calm);
    }
  };

  const reset = () => { setStep(0); setAnswers([]); setResult(null); };

  return (
    <section className="py-20 px-6 bg-gradient-to-b from-[#faf8f5] to-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#9D4EDD]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#FF6B9D]/5 rounded-full blur-3xl" />

      <div className="max-w-2xl mx-auto relative z-10">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#9D4EDD]/10 border border-[#9D4EDD]/20 mb-4">
            <Sparkles className="w-3.5 h-3.5 text-[#9D4EDD]" />
            <span className="text-xs text-[#9D4EDD] uppercase tracking-[3px] font-bold">30-Second Crystal Match</span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl text-[#1e1919] mb-3">Which Crystal Is Calling You?</h2>
          <p className="text-[#7A7A9A] text-sm">Answer 3 questions. Get your personalized crystal recommendation.</p>
        </div>

        {!result ? (
          <div className="bg-white rounded-3xl p-8 shadow-xl border border-[#e8e4de]" data-testid="crystal-quiz-card">
            {/* Progress */}
            <div className="flex gap-2 mb-6">
              {QUESTIONS.map((_, i) => (
                <div key={i} className={`h-1 flex-1 rounded-full transition-all ${i <= step ? 'bg-gradient-to-r from-[#9D4EDD] to-[#FF6B9D]' : 'bg-[#e8e4de]'}`} />
              ))}
            </div>

            <p className="text-xs text-[#9D4EDD] uppercase tracking-wider mb-2">Question {step + 1} of {QUESTIONS.length}</p>
            <h3 className="font-serif text-xl text-[#1e1919] mb-6">{QUESTIONS[step].q}</h3>

            <div className="grid sm:grid-cols-2 gap-3">
              {QUESTIONS[step].options.map((opt) => {
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.value}
                    onClick={() => handleAnswer(opt.value)}
                    className="p-4 rounded-xl border-2 border-transparent bg-[#faf8f5] hover:border-[#9D4EDD] hover:bg-[#f8f5ff] transition-all text-left flex items-center gap-3 group"
                    data-testid={`quiz-option-${opt.value}`}
                  >
                    {Icon && <Icon className="w-5 h-5 text-[#9D4EDD] group-hover:scale-110 transition-transform" />}
                    <span className="text-sm font-medium text-[#4A4A6A] group-hover:text-[#1e1919]">{opt.label}</span>
                    <ChevronRight className="w-4 h-4 text-[#e8e4de] group-hover:text-[#9D4EDD] ml-auto transition-colors" />
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-3xl p-8 shadow-xl border border-[#e8e4de] text-center" data-testid="crystal-quiz-result">
            <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ background: `linear-gradient(135deg, ${result.color}30, ${result.color}10)` }}>
              <Moon className="w-8 h-8" style={{ color: result.color }} />
            </div>
            <p className="text-xs text-[#c9a96e] uppercase tracking-wider mb-2">Your Crystal Match</p>
            <h3 className="font-serif text-3xl mb-3" style={{ color: result.color }}>{result.crystal}</h3>
            <p className="text-[#61525a] text-sm leading-relaxed mb-6 max-w-md mx-auto">{result.message}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/healing-prescription"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#9D4EDD] to-[#FF6B9D] text-white rounded-xl font-bold hover:shadow-xl transition-all"
                data-testid="quiz-get-prescription"
              >
                Get Full Prescription — $125 <ArrowRight className="w-4 h-4" />
              </Link>
              <button onClick={reset} className="text-sm text-[#9D4EDD] hover:text-[#FF6B9D] transition-colors">Retake Quiz</button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default QuickCrystalQuiz;
